import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const ACC_TYPES = ["Lumper", "Detention", "Layover", "TONU", "Other"];

// item: null = closed, {} = add, accessorial = edit
export const AccessorialDialog = ({ item, onClose, onSave }) => {
  const [f, setF] = useState({ type: "Detention", label: "", amount: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (item) {
      setF({ type: item.type || "Detention", label: item.label || "", amount: item.amount ? String(item.amount) : "", note: item.note || "" });
      setError("");
    }
  }, [item]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const valid = parseFloat(f.amount) > 0 && (f.type !== "Other" || f.label.trim());
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      await onSave({ type: f.type, label: f.type === "Other" ? f.label.trim() : null, amount: parseFloat(f.amount), note: f.note.trim() || null });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save this charge");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="accessorial-dialog">
        <DialogHeader><DialogTitle>{item?.id ? "Edit accessorial" : "Add accessorial"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Type</Label>
            <Select value={f.type} onValueChange={set("type")}>
              <SelectTrigger className="mt-1" data-testid="acc-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ACC_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`acc-type-${t.toLowerCase()}`}>{t === "Other" ? "Other (custom)" : t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {f.type === "Other" && (
            <div><Label htmlFor="acc-label">Charge name</Label><Input id="acc-label" value={f.label} onChange={set("label")} placeholder="e.g. Tarp fee" maxLength={80} className="mt-1" data-testid="acc-label-input" /></div>
          )}
          <div><Label htmlFor="acc-amount">Amount (USD)</Label><Input id="acc-amount" type="number" step="0.01" min="0" value={f.amount} onChange={set("amount")} placeholder="0.00" className="mt-1" data-testid="acc-amount-input" /></div>
          {f.type === "Lumper" && <p className="text-xs text-amber-800" data-testid="acc-lumper-hint">Lumper receipts the driver marks &quot;We paid&quot; are already billed automatically. Only add a lumper here if there&apos;s no receipt, so it isn&apos;t billed twice.</p>}
          <div><Label htmlFor="acc-note">Note (optional)</Label><Input id="acc-note" value={f.note} onChange={set("note")} placeholder="e.g. 3 hrs at receiver" maxLength={300} className="mt-1" data-testid="acc-note-input" /></div>
          {error && <p className="text-sm text-rose-600" data-testid="acc-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="acc-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={!valid || busy} className="bg-[#002FA7] hover:bg-[#001A75]" data-testid="acc-save-btn">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const LumperEditDialog = ({ doc, onClose, onSave }) => {
  const [paidBy, setPaidBy] = useState("carrier");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (doc) { setPaidBy(doc.paid_by || "carrier"); setAmount(doc.amount ? String(doc.amount) : ""); setError(""); }
  }, [doc]);
  const valid = paidBy === "broker" || parseFloat(amount) > 0;
  const save = async () => {
    setBusy(true);
    try {
      await onSave({ paid_by: paidBy, amount: parseFloat(amount) || 0 });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!doc} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="lumper-edit-dialog">
        <DialogHeader><DialogTitle>Lumper receipt</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {[["carrier", "We paid (carrier)", "Billed back on invoice"], ["broker", "Broker paid", "Info only, not billed"]].map(([k, l, s]) => (
              <button key={k} type="button" onClick={() => setPaidBy(k)} aria-pressed={paidBy === k} className={`rounded-lg border-2 p-3 text-left ${paidBy === k ? "border-[#002FA7] bg-blue-50" : "border-slate-200"}`} data-testid={`lumper-edit-paid-${k}`}>
                <div className="text-sm font-semibold">{l}</div><div className="text-[11px] text-slate-500">{s}</div>
              </button>
            ))}
          </div>
          <div><Label htmlFor="lumper-amt">Amount (USD){paidBy === "broker" ? " (optional)" : ""}</Label><Input id="lumper-amt" type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="lumper-edit-amount" /></div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={!valid || busy} className="bg-[#002FA7] hover:bg-[#001A75]" data-testid="lumper-edit-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
